import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaUserGraduate,
  FaUserTie,
  FaUserShield,
  FaUserNurse,
  FaTooth,
  FaLaptopCode,
  FaExternalLinkAlt,
  FaInfoCircle,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

const ROLE_META = {
  student: { label: 'Student', icon: FaUserGraduate, accent: 'from-[#0077B6] to-[#00A6ED]' },
  supervisor: { label: 'Supervisor', icon: FaUserTie, accent: 'from-[#7048E8] to-[#9775FA]' },
  admin: { label: 'Admin', icon: FaUserShield, accent: 'from-[#20B2AA] to-[#38D9A9]' },
  assistant: { label: 'Assistant', icon: FaUserNurse, accent: 'from-[#F7931E] to-[#FFB84D]' },
  dentist: { label: 'Dentist', icon: FaTooth, accent: 'from-[#E86BA0] to-[#FAA2C1]' },
  it: { label: 'IT', icon: FaLaptopCode, accent: 'from-[#495057] to-[#868E96]' },
};

/**
 * Opens any role's dashboard in a new tab, signed in as that role's test
 * account, without disturbing the superadmin's own session.
 *
 * Each tab gets its own session (see AuthContext), so several roles can be open
 * side by side. The button never receives a token: it asks the server for a
 * single-use code and hands that to the new tab, which trades it for a session.
 */
const RoleSwitcher = ({ token }) => {
  const [targets, setTargets] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/superadmin/impersonation/targets`,
          authConfig()
        );
        if (!active) return;
        setEnabled(res.data.enabled !== false);
        setTargets(res.data.targets || []);
      } catch (err) {
        if (!active) return;
        setError('Could not load the available roles.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [authConfig]);

  if (!enabled) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8">
      <div className="px-5 sm:px-6 pt-5 pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Open a role dashboard</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Opens in a new tab as that role&apos;s test account. Your superadmin
            session stays signed in here.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 self-start">
          <FaInfoCircle className="h-3 w-3" />
          Superadmin only
        </span>
      </div>

      {error && (
        <div className="mx-5 sm:mx-6 mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      <div className="px-5 sm:px-6 pb-5">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {targets.map((target) => {
              const meta = ROLE_META[target.role] || {};
              const Icon = meta.icon || FaUserShield;

              // A real link, not a scripted window.open: browsers never
              // pop-up-block a genuine target="_blank" navigation, and the URL
              // carries only a role name - no credential.
              const Wrapper = target.available ? 'a' : 'div';
              const wrapperProps = target.available
                ? {
                    href: `/impersonate?role=${encodeURIComponent(target.role)}`,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {};

              return (
                <Wrapper
                  key={target.role}
                  {...wrapperProps}
                  title={
                    target.available
                      ? `Open the ${meta.label || target.role} dashboard as ${target.email} in a new tab`
                      : `No test account found for ${target.email}`
                  }
                  className={`group relative block rounded-xl p-3 text-left transition-all border no-underline ${
                    target.available
                      ? 'border-gray-100 hover:border-transparent hover:shadow-md bg-white'
                      : 'border-dashed border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  }`}
                >
                  <span
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-lg mb-2 text-white bg-gradient-to-br ${
                      target.available ? (meta.accent || 'from-gray-400 to-gray-500') : 'from-gray-300 to-gray-400'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </span>

                  <span className="block text-sm font-semibold text-gray-800">
                    {meta.label || target.role}
                  </span>
                  <span className="block text-[11px] text-gray-400 truncate">
                    {target.available ? (target.name || target.email) : 'No test account'}
                  </span>

                  {target.available && (
                    <FaExternalLinkAlt className="absolute top-3 right-3 h-3 w-3 text-gray-300 group-hover:text-[#0077B6] transition-colors" />
                  )}
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleSwitcher;
